import type { UnresolvedCommitDescriptor } from 'custom-types/UnresolvedCommitDescriptor';
import { type ReactNode, type RefObject } from 'react';
import { useNavigationHash } from 'ts/base/hooks/UseNavigationHash';
import { COVERAGE_SETTINGS_DEFAULT } from 'ts/base/routing/search/schema/CoverageSettings';
import { CoverageMode } from 'ts/commons/code/CoverageModeSelector';
import { RenderedCode } from 'ts/commons/code/RenderedCode';
import type { CodeContainerImperativeHandle, VisibleCodeRegion } from 'ts/commons/code/RenderedCode.context';
import type { CodeDiff } from 'ts/commons/code/RenderedCode.diff';
import { DateUtils } from 'ts/commons/DateUtils';
import type { DiffDescription } from 'typedefs/DiffDescription';
import type { FindingContent } from 'typedefs/FindingContent';
import type { FormattedTokenElementInfo } from 'typedefs/FormattedTokenElementInfo';
import type { LineCoverageInfo } from 'typedefs/LineCoverageInfo';
import type { RepositoryLogEntry } from 'typedefs/RepositoryLogEntry';
import type { SelfContainedFinding } from 'typedefs/SelfContainedFinding';
import type { TrackedFinding } from 'typedefs/TrackedFinding';
import type { WrappedLogEntry } from 'typedefs/WrappedLogEntry';

/** Describes the content of one side for a compare view. */
export type CompareContentInfo = {
	readonly project: string;
	readonly uniformPath: string;
	/**
	 * The commit for which the code is displayed. May be <code>null</code> to indicate the most recent commit on the
	 * default branch.
	 */
	readonly commit: UnresolvedCommitDescriptor | null;
	/** The line to navigate to. May be null to indicate no specific line. */
	readonly initialLine?: number;
	/**
	 * The line to start of the region the diff should focus on. If onlyShowRegion is false (default) in
	 * CompareCodeComponent the full file will be visible, but changes will only be highlighted for the selected region.
	 * If it is true, only this range will be visible in the compare view.
	 */
	readonly region?: VisibleCodeRegion;
	/** Whether coverage should NOT be calculated for this side. */
	readonly skipCoverageCalculation?: boolean;
	readonly mergeRequestId?: string;
};

/** The data that has been loaded from the backend based on CompareContentInfo. */
export type CompareContentData = {
	content: FormattedTokenElementInfo | FindingContent | null;
	findings: TrackedFinding[] | SelfContainedFinding[];
	logEntry: RepositoryLogEntry | WrappedLogEntry | null;
	lineCoverageInfo?: LineCoverageInfo | null;
};

type CompareContentSideProps = {
	info: CompareContentInfo;
	data: CompareContentData;
	diffDescription: DiffDescription;
	initialLine: number | undefined;
	/** Whether this is the left or right source of the compare. */
	side: 'left' | 'right';
	/** Whether the differences to the previous Commit should get highlighted or not. */
	highlightInLastCommitChangedRegions?: boolean;
	/** Whether to annotate the code with line coverage. */
	showTestCoverage: boolean;
	codeFontSize: number;
	onScroll: () => void;

	/**
	 * The regions which should be highlighted yellow. Contains the character offsets, which are absolute to the start
	 * of the file, organized in pairs (e.g. start of change region at index 0 and end at index 1).
	 */
	changeRegionsPreviousCommit?: number[];
	codeHandleRef?: RefObject<CodeContainerImperativeHandle | null>;
	visibleRegion?: VisibleCodeRegion;
};

/**
 * Renders the source code for this content into the given parent element. If no content is available, a "file not
 * found" or a "file did not exist at the branching point of the merge request"-message is rendered instead.
 */
export function CompareContentSide({
	info,
	data,
	diffDescription,
	initialLine,
	side,
	highlightInLastCommitChangedRegions,
	showTestCoverage,
	codeFontSize,
	onScroll,
	changeRegionsPreviousCommit,
	codeHandleRef,
	visibleRegion
}: CompareContentSideProps): ReactNode {
	// If we show comparison for a merge request and the left side is missing, we assume that the file was newly added in the merge request.
	const hasMergeRequestId = useNavigationHash().getString('merge-request') != null;
	if (data.content == null || data.content.uniformPath === '') {
		if (hasMergeRequestId) {
			return 'This file did not exist at the branching point of the merge request.';
		}
		return <FileNotFound filepath={info.uniformPath} commit={info.commit} />;
	}
	let inLastCommitChangedRegions: number[] | null = null;
	if (highlightInLastCommitChangedRegions && changeRegionsPreviousCommit != null) {
		inLastCommitChangedRegions = changeRegionsPreviousCommit;
	}
	const diff: CodeDiff = {
		side,
		description: diffDescription,
		inLastCommitChangedRegions
	};
	return (
		<RenderedCode
			initialScrollToLine={initialLine ? { line: initialLine, align: 'start' } : undefined}
			coverageSettings={{ ...COVERAGE_SETTINGS_DEFAULT, coverageMode: CoverageMode.TEST_COVERAGE }}
			hideContextMenu
			hideOutline
			codeElement={data.content}
			project={info.project}
			commit={info.commit!}
			diff={diff}
			fontSize={codeFontSize}
			findings={data.findings}
			onScroll={onScroll}
			imperativeHandleRef={codeHandleRef}
			visibleRegion={visibleRegion}
			lineCoverage={showTestCoverage ? (data.lineCoverageInfo ?? undefined) : undefined}
		/>
	);
}

type FileNotFoundProps = {
	filepath: string;
	commit: UnresolvedCommitDescriptor | null;
};

function FileNotFound({ filepath, commit }: FileNotFoundProps) {
	let formattedTimestamp = 'unknown';
	if (commit != null) {
		formattedTimestamp = DateUtils.formatTimestamp(commit.getTimestamp());
	}
	return (
		<div className="px-[10px] py-1">
			Did not find file {filepath}
			<br />
			at {formattedTimestamp}.
			<br />
			File may not have existed at that point.
		</div>
	);
}
